import type { PassCell } from '../../api/types';
import { assetUrl, RARITY_COLOR, RARITY_TINT_A, rarityTint } from '../cosmetics/cosmeticsMap';
import { CosmeticAvatar } from '../cosmetics/CosmeticAvatar';
import { NickFx } from '../cosmetics/NickFx';
import { DeckBox } from './DeckBox';

/** Глифы слотов (inline SVG, currentColor) — превью предмета в ячейках. */
export function SlotGlyph({ slot, size = 20 }: { slot: string; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (slot) {
    case 'frame':
      return (
        <svg {...common} aria-hidden>
          <circle cx="12" cy="12" r="8.4" />
          <circle cx="12" cy="12" r="4.6" />
        </svg>
      );
    case 'nick':
      return (
        <svg {...common} aria-hidden>
          <path d="M5 19L12 4.5 19 19M7.6 14h8.8" />
        </svg>
      );
    case 'row':
      return (
        <svg {...common} aria-hidden>
          <rect x="3" y="8" width="18" height="8" rx="3" />
          <path d="M6.6 12h5" />
        </svg>
      );
    case 'bg':
      return (
        <svg {...common} aria-hidden>
          <rect x="3.4" y="5" width="17.2" height="14" rx="2.6" />
          <path d="M3.4 15.4 9 10.6l4 3.4 3-2.4 4.6 3.8" />
        </svg>
      );
    default:
      return null;
  }
}

export function VoucherGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h14a1.5 1.5 0 0 1 1.5 1.5v2.2a2.3 2.3 0 0 0 0 4.6v2.2A1.5 1.5 0 0 1 19 18H5a1.5 1.5 0 0 1-1.5-1.5v-2.2a2.3 2.3 0 0 0 0-4.6z" />
      <path d="M14.5 6.5v11" />
    </svg>
  );
}

/**
 * Превью награды в ячейке дорожки/сетке: настоящий предмет (BP Reward) —
 * рамка на аватаре-букве, ник с эффектом, мини-строка, фон-webp, колода,
 * ваучер-билетик. Рамка/фон в цвете редкости только у доступной (`active`),
 * у остальных — нейтральные --border/--sunk (макет 6.1).
 */
export function RewardPreview({
  cell,
  size = 50,
  active = false,
}: {
  cell: PassCell;
  size?: number;
  active?: boolean;
}) {
  const rarity = cell.kind === 'deck' ? (cell.deckRarity ?? 0) : (cell.item?.rarity ?? 0);
  const color = RARITY_COLOR[rarity];
  const style = {
    width: size,
    height: size,
    color,
    borderColor: active ? color : 'var(--border)',
    background: active ? rarityTint(rarity, RARITY_TINT_A[rarity] ?? 0.14) : 'var(--sunk)',
  };
  if (cell.kind === 'deck') {
    return (
      <span className="bp-preview" style={style}>
        <DeckBox rarity={rarity} stage="idle" size={Math.round(size * 0.42)} />
      </span>
    );
  }
  if (cell.kind === 'voucher') {
    // макет: ваучер всегда с золотой обводкой и tint
    return (
      <span
        className="bp-preview"
        style={{
          ...style,
          color: 'var(--gold)',
          borderColor: 'rgba(200, 160, 78, 0.55)',
          background: 'rgba(200, 160, 78, 0.18)',
        }}
      >
        <VoucherGlyph size={Math.round(size * 0.48)} />
      </span>
    );
  }
  const item = cell.item;
  const url = item?.kind === 'webp' ? assetUrl(item.asset) : null;
  if (url) {
    return (
      <span className="bp-preview" style={style}>
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </span>
    );
  }
  if (item?.slot === 'frame') {
    return (
      <span className="bp-preview" style={style}>
        <CosmeticAvatar name="Игрок" size={Math.round(size * 0.68)} frame={item} />
      </span>
    );
  }
  if (item?.slot === 'nick' && item.kind === 'css') {
    return (
      <span className="bp-preview" style={style}>
        <span style={{ font: '800 10.5px var(--font-ui)' }}>
          <NickFx cosmetic={item}>Ник</NickFx>
        </span>
      </span>
    );
  }
  if (item?.slot === 'row') {
    return (
      <span className="bp-preview" style={style}>
        <span className="bp-prev-minirow" aria-hidden>
          <i className="dot" />
          <i className="line" />
        </span>
      </span>
    );
  }
  return (
    <span className="bp-preview" style={style}>
      <SlotGlyph slot={item?.slot ?? (cell.meta === 'random_nick_color' ? 'nick' : 'row')} size={Math.round(size * 0.46)} />
    </span>
  );
}
